import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FormField } from '@/shared/ui/FormField';
import { deleteVerificationFile, editLegal, fetchLegal, type LegalData } from './api';
import { LEGAL_LABELS } from './legalLabels';
import { ReviewPanel } from './ReviewPanel';
import { SectionLayout } from './SectionLayout';
import { getSectionStatus, useRequestId } from './sectionHelpers';
import { useCanEditVerification } from './useCanEditVerification';

const LegalForm = ({ requestId, data }: { requestId: number; data: LegalData }) => {
  const qc = useQueryClient();
  const canEdit = useCanEditVerification();
  const [entityCard, setEntityCard] = useState<Record<string, string>>(data.entity_card ?? {});
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const status = getSectionStatus(data.status);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['verification', 'legal', requestId] });

  const { errors, clearErrors, save, isPending } = useSaveForm(
    (card: Record<string, string>) => editLegal(requestId, card),
    () => {
      setDirty(false);
      setSaved(true);
      void invalidate();
    },
  );
  const removeFile = useMutation({ mutationFn: deleteVerificationFile, onSuccess: invalidate });

  const commonErrors = [...(errors.legal_data ?? []), ...(errors.common ?? [])];

  return (
    <>
      <h3 style={{ fontSize: 18 }}>Документы</h3>
      <div style={{ marginBottom: 24 }}>
        <b>Договор:</b>
        {(data.contract ?? []).map((file) => (
          <div key={file.id}>
            <a href={file.url} download>
              {file.name}
            </a>{' '}
            <button
              type="button"
              className="btn btn-danger btn-xs"
              disabled={removeFile.isPending}
              onClick={() => removeFile.mutate(file.id)}
            >
              <i className="fa fa-close" />
            </button>
          </div>
        ))}
      </div>
      <h3 style={{ fontSize: 18 }}>Юридическая информация</h3>
      {Object.entries(entityCard).map(([field, value]) => (
        <FormField key={field} label={LEGAL_LABELS[field] ?? field} errors={errors[field]}>
          <input
            className="form-control input-sm"
            style={{ maxWidth: 500 }}
            value={value ?? ''}
            readOnly={!canEdit}
            onChange={(e) => {
              setEntityCard((prev) => ({ ...prev, [field]: e.target.value }));
              setDirty(true);
              setSaved(false);
              clearErrors(field);
            }}
          />
        </FormField>
      ))}
      <ReviewPanel
        requestId={requestId}
        section="legal_data"
        status={status}
        initialRejectMessage={data.reject_message}
        disableVerify={dirty}
        feedback={
          <>
            {commonErrors.map((message) => (
              <p key={message} className="text-red" style={{ marginTop: 10 }}>
                {message}
              </p>
            ))}
            {saved && <p className="text-green">Успешно</p>}
          </>
        }
        actions={
          dirty && canEdit ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ marginRight: 12 }}
              disabled={isPending}
              onClick={() => save(entityCard)}
            >
              Сохранить
            </button>
          ) : null
        }
      />
    </>
  );
};

export const VerificationLegalPage = () => {
  const requestId = useRequestId();
  const { data, isLoading, error } = useQuery({
    queryKey: ['verification', 'legal', requestId],
    queryFn: () => fetchLegal(requestId),
  });

  return (
    <SectionLayout
      title="Юридическая информация"
      status={getSectionStatus(data?.status)}
      isLoading={isLoading}
      error={error}
    >
      {data && <LegalForm key={requestId} requestId={requestId} data={data} />}
    </SectionLayout>
  );
};
